import { FunctionComponent } from 'react';
import styled from 'styled-components';
import { InfoNotification, WarningNotification } from '@ardoq/status-ui';
import { t, type I18nLocale } from '@ardoq/i18n';

const DivWithMargin = styled.div`
  margin-top: 16px;
  margin-bottom: 16px;
`;

const getComponentNoun = (count: number, i18nLocale: I18nLocale): string =>
  count === 1
    ? t(i18nLocale, 'confirmDeletion:component.nounSingular', 'component')
    : t(i18nLocale, 'confirmDeletion:component.nounPlural', 'components');

const getReferenceNoun = (count: number, i18nLocale: I18nLocale): string =>
  count === 1
    ? t(i18nLocale, 'confirmDeletion:reference.nounSingular', 'reference')
    : t(i18nLocale, 'confirmDeletion:reference.nounPlural', 'references');

export const ComponentMessage: FunctionComponent<{
  componentNames: string[];
  numOfComponents: number;
  numOfReferences: number;
  hasSubtreeInfo: boolean;
  i18nLocale: I18nLocale;
}> = ({
  componentNames,
  numOfComponents,
  numOfReferences,
  hasSubtreeInfo,
  i18nLocale,
}) => {
  const compNoun = getComponentNoun(numOfComponents, i18nLocale);
  const refNoun = getReferenceNoun(numOfReferences, i18nLocale);
  const themOrIt =
    numOfComponents > 1
      ? t(i18nLocale, 'confirmDeletion:component.them', 'them')
      : t(i18nLocale, 'confirmDeletion:component.it', 'it');
  return (
    <>
      <WarningNotification style={{ marginBottom: 8 }}>
        {hasSubtreeInfo
          ? t(
              i18nLocale,
              'confirmDeletion:component.totalDelete',
              {
                numOfComponents,
                compNoun,
                numOfReferences,
                refNoun,
              },
              'In total this will delete {{numOfComponents}} {{compNoun}} and {{numOfReferences}} {{refNoun}}.'
            )
          : t(
              i18nLocale,
              'confirmDeletion:component.unknownTotal',
              'The total of deleted components and references is unknown'
            )}
      </WarningNotification>

      <div>
        {t(
          i18nLocale,
          'confirmDeletion:component.willBeDeleted',
          { compNoun, themOrIt },
          'The following {{compNoun}}, the children and references to and from {{themOrIt}} will be deleted:'
        )}
        <DivWithMargin>
          <ul>
            {componentNames.map((name, index) => (
              <li key={name + index}>{name}</li>
            ))}
          </ul>
        </DivWithMargin>
      </div>
    </>
  );
};

export const ReferenceMessage: FunctionComponent<{
  referenceNames: string[];
  additionally?: boolean;
  i18nLocale: I18nLocale;
}> = ({ referenceNames, additionally = false, i18nLocale }) => {
  const refNoun = getReferenceNoun(referenceNames.length, i18nLocale);
  return (
    <div>
      <span>
        {additionally
          ? t(
              i18nLocale,
              'confirmDeletion:reference.additionallyWillBeDeleted',
              { refNoun },
              'Additionally, the following {{refNoun}} will be deleted:'
            )
          : t(
              i18nLocale,
              'confirmDeletion:reference.willBeDeleted',
              { refNoun },
              'The following {{refNoun}} will be deleted:'
            )}
      </span>
      <ul>
        {referenceNames.map((name, index) => (
          <li key={name + index}>{name}</li>
        ))}
      </ul>
      <InfoNotification>
        {t(
          i18nLocale,
          'confirmDeletion:reference.referencedStillInData',
          { refNoun },
          "The referenced components will still be in your organization's data, but they may disappear from your current view. Please note that only the {{refNoun}} will be deleted."
        )}
      </InfoNotification>
    </div>
  );
};
